import { DestroyRef, Directive, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ColumnBase, CommandColumnComponent, GridComponent, PDFComponent } from '@progress/kendo-angular-grid';

/**
 * Exports the grid's own columns, minus the actions column (#199).
 *
 *   <kendo-grid-pdf appGridPdf fileName="Verkäufe.pdf" [allPages]="true" …>
 *
 * Kendo draws a PDF from the grid as displayed, actions included; the only way to leave the
 * actions column out is to declare the export's columns inside `kendo-grid-pdf`. Ten grids did,
 * each a hand-copied second list of its columns, and the copies drifted — two PDFs printed an
 * empty „Kunde" column from a field the row does not have. This directive supplies that list at
 * export time instead, from whatever the grid shows right then: conditional columns included,
 * command columns dropped. A `kendo-grid-pdf` that declares columns of its own keeps them.
 *
 * It leans on one detail Kendo does not document for this use: `PDFComponent.columns` is the
 * content query the export reads, and resetting it before `pdfExport` reaches `saveAsPDF` is
 * what the declared-columns path does internally. `tests/pdf_export.spec.ts` downloads a real
 * export, so a Kendo upgrade that changes this fails loudly rather than silently.
 */
@Directive({ selector: 'kendo-grid-pdf[appGridPdf]' })
export class GridPdfColumnsDirective {
  private readonly pdf = inject(PDFComponent, { self: true });
  private readonly grid = inject(GridComponent);

  constructor() {
    this.grid.pdfExport.pipe(takeUntilDestroyed(inject(DestroyRef))).subscribe(() => this.supplyColumns());
  }

  private declaresOwnColumns: boolean | undefined;

  private supplyColumns(): void {
    // Decided on the first export, before this directive has ever written to the list.
    this.declaresOwnColumns ??= this.pdf.columns.length > 0;
    if (this.declaresOwnColumns) {
      return;
    }
    this.pdf.columns.reset(this.exportableColumns());
  }

  /** The grid's current columns without the command (actions) columns. */
  exportableColumns(): ColumnBase[] {
    return this.grid.columns.toArray().filter((column) => !(column instanceof CommandColumnComponent));
  }
}
